'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Scan, FileText, Truck, Hash, ClipboardCheck, CheckCircle2, RotateCcw, ChevronRight, AlertTriangle, Package } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import GRNSuccessPanel from './GRNSuccessPanel';
import GRNRecentList from './GRNRecentList';

interface GRNFormData {
  invoiceNumber: string;
  deliveryNote: string;
  poReference: string;
  challanNumber: string;
  supplierName: string;
  vehicleNumber: string;
  remarks: string;
}

const EMPTY_FORM: GRNFormData = {
  invoiceNumber: '',
  deliveryNote: '',
  poReference: '',
  challanNumber: '',
  supplierName: '',
  vehicleNumber: '',
  remarks: '',
};

function generateGRNId(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const seq = String(Math.floor(Math.random() * 900) + 100);
  return `GRN-${yyyy}${mm}${dd}-${seq}`;
}

interface ScanFieldProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  icon: React.ReactNode;
  required?: boolean;
  hint?: string;
}

function ScanField({ label, placeholder, value, onChange, icon, required, hint }: ScanFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative flex items-center">
        <span className="absolute left-3 text-muted-foreground">{icon}</span>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-muted/40 border border-border rounded-lg pl-9 pr-10 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.focus()}
          className="absolute right-2.5 p-1 rounded text-muted-foreground hover:text-primary transition-colors"
          title="Scan barcode"
        >
          <Scan size={15} />
        </button>
      </div>
      {hint && <p className="text-2xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function GRNRegistrationForm() {
  const [form, setForm] = useState<GRNFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [generatedGRN, setGeneratedGRN] = useState<string | null>(null);
  const [recentGRNs, setRecentGRNs] = useState<Array<{ id: string; invoice: string; supplier: string; time: string }>>([]);

  const setField = useCallback((field: keyof GRNFormData) => (v: string) => {
    setForm((prev) => ({ ...prev, [field]: v }));
  }, []);

  const isValid = form.invoiceNumber.trim() && form.poReference.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setTimeout(() => {
      const grnId = generateGRNId();
      setGeneratedGRN(grnId);
      setRecentGRNs((prev) => [
        {
          id: grnId,
          invoice: form.invoiceNumber,
          supplier: form.supplierName || 'Unknown Supplier',
          time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
        },
        ...prev.slice(0, 4),
      ]);
      setSubmitting(false);
    }, 800);
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setGeneratedGRN(null);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: Registration Form ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Status banner */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-yellow-500/8 border border-yellow-500/20">
              <AlertTriangle size={15} className="text-accent flex-shrink-0" />
              <p className="text-xs text-accent font-medium">
                All registered GRNs initialize inventory in <strong>Quarantine</strong> status — stock will not be available for picking, kitting, or dispatch until QC approval.
              </p>
            </div>

            {/* Form card */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-primary" />
                  <h2 className="text-sm font-bold text-foreground">GRN Entry Form</h2>
                </div>
                <StatusBadge status="quarantine" size="sm" />
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-5">
                {/* Row 1: Invoice + Delivery Note */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ScanField
                    label="Invoice Number"
                    placeholder="e.g. INV-2026-00123"
                    value={form.invoiceNumber}
                    onChange={setField('invoiceNumber')}
                    icon={<FileText size={15} />}
                    required
                    hint="Scan or type supplier invoice number"
                  />
                  <ScanField
                    label="Delivery Note"
                    placeholder="e.g. DN-00456"
                    value={form.deliveryNote}
                    onChange={setField('deliveryNote')}
                    icon={<ClipboardCheck size={15} />}
                    hint="Delivery note / packing slip number"
                  />
                </div>

                {/* Row 2: PO Reference + Challan */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ScanField
                    label="PO Reference"
                    placeholder="e.g. PO-2026-0078"
                    value={form.poReference}
                    onChange={setField('poReference')}
                    icon={<Hash size={15} />}
                    required
                    hint="Purchase order reference number"
                  />
                  <ScanField
                    label="Challan Number"
                    placeholder="e.g. CH-00789"
                    value={form.challanNumber}
                    onChange={setField('challanNumber')}
                    icon={<FileText size={15} />}
                    hint="Transport / e-way bill challan"
                  />
                </div>

                {/* Row 3: Supplier + Vehicle */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Supplier Name
                    </label>
                    <input
                      type="text"
                      value={form.supplierName}
                      onChange={(e) => setField('supplierName')(e.target.value)}
                      placeholder="e.g. MedSupply India Pvt Ltd"
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Truck size={13} />
                      Vehicle Number
                    </label>
                    <input
                      type="text"
                      value={form.vehicleNumber}
                      onChange={(e) => setField('vehicleNumber')(e.target.value)}
                      placeholder="e.g. MH-12-AB-1234"
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Remarks
                  </label>
                  <textarea
                    value={form.remarks}
                    onChange={(e) => setField('remarks')(e.target.value)}
                    placeholder="Any notes about this shipment..."
                    rows={2}
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all resize-none"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={!isValid || submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-all"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Generating GRN…
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={15} />
                        Register GRN &amp; Initialize Quarantine
                        <ChevronRight size={14} />
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                  >
                    <RotateCcw size={14} />
                    Clear
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* ── Right: GRN Output + Recent ── */}
          <div className="space-y-5">
            <GRNSuccessPanel grnId={generatedGRN} formData={form} onNewGRN={handleReset} />
            <GRNRecentList items={recentGRNs} />
          </div>
        </div>
      </div>
    </div>
  );
}
